import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const lifecycleMeta: InsightMeta = {
  slug: "after-iso-certification-surveillance-audits-and-renewal",
  metaTitle: {
    en: "ISO Surveillance Audit and Renewal in Iraq: Full Guide",
    ar: "تدقيق المراقبة وتجديد شهادة الأيزو في العراق: الدليل الكامل",
  },
  title: {
    en: "What Happens After You Get ISO Certified in Iraq: Surveillance Audits and Renewal",
    ar: "ماذا يحدث بعد حصولك على شهادة الأيزو في العراق: تدقيق المراقبة والتجديد",
  },
  description: {
    en: "Plan surveillance, corrective actions, recertification and certificate transfer with your certification body. Early scheduling does not guarantee continuous validity.",
    ar: "خطّط لتدقيق المراقبة والإجراءات التصحيحية وإعادة منح الشهادة ونقلها مع الجهة المانحة. لا يضمن الحجز المبكر استمرار الصلاحية دون انقطاع.",
  },
  datePublished: "2026-07-29",
  dateModified: "2026-10-09",
  dateLabel: { en: "9 October 2026", ar: "9 تشرين الأول 2026" },
  faq: [
    {
      q: { en: "What is an ISO surveillance audit?", ar: "ما هو تدقيق المراقبة في الأيزو؟" },
      a: {
        en: "A surveillance audit checks continued conformity of the certified management system. In a typical three-year cycle it takes place between initial certification and recertification. Confirm the actual schedule and scope with your certification body.",
        ar: "يفحص تدقيق المراقبة استمرار مطابقة نظام الإدارة الحاصل على الشهادة. وفي الدورة المعتادة البالغة ثلاث سنوات، يُجرى بين المنح الأول وإعادة منح الشهادة. أكّد الجدول الفعلي والنطاق مع الجهة المانحة.",
      },
    },
    {
      q: {
        en: "What happens if I fail an ISO audit in Iraq?",
        ar: "ماذا يحدث إذا أخفقت في تدقيق الأيزو في العراق؟",
      },
      a: {
        en: "The certification body classifies findings, sets the required response and decides their effect on certification. Submit corrective actions and evidence by its deadlines. A response or completed audit does not itself guarantee that certification is maintained.",
        ar: "تصنّف الجهة المانحة الملاحظات وتحدد الاستجابة المطلوبة وتقرر أثرها على الشهادة. قدّم الإجراءات التصحيحية والأدلة ضمن المهل المحددة. ولا تضمن الاستجابة أو إكمال التدقيق وحدهما استمرار الشهادة.",
      },
    },
    {
      q: {
        en: "What happens if I miss a surveillance audit?",
        ar: "ماذا يحدث إذا فوت تدقيق المراقبة؟",
      },
      a: {
        en: "Contact the certification body immediately. Failure to complete required surveillance can lead to suspension or withdrawal. Obtain written confirmation of status and the steps needed; do not present a suspended or expired certificate as valid.",
        ar: "تواصل فوراً مع الجهة المانحة. قد يؤدي عدم استكمال المراقبة المطلوبة إلى تعليق الشهادة أو سحبها. احصل على تأكيد مكتوب للحالة والخطوات اللازمة، ولا تقدّم شهادة معلّقة أو منتهية باعتبارها سارية.",
      },
    },
    {
      q: {
        en: "Can I transfer my ISO certificate to another certification body?",
        ar: "هل يمكنني نقل شهادة الأيزو إلى جهة مانحة أخرى؟",
      },
      a: {
        en: "Transfer requires the receiving certification body’s review of eligibility, accreditation, status, audit history and outstanding findings. Ask it to confirm the applicable transfer rules and remaining cycle before ending the existing arrangement. Transfer is not automatic.",
        ar: "يتطلب النقل مراجعة الجهة المانحة المستلمة للأهلية والاعتماد والحالة وسجل التدقيق والملاحظات المفتوحة. اطلب منها تأكيد قواعد النقل المنطبقة والمدة المتبقية من الدورة قبل إنهاء الترتيب الحالي. فالنقل ليس تلقائياً.",
      },
    },
    {
      q: {
        en: "Can I use the ISO logo on my products?",
        ar: "هل يمكنني وضع شعار الأيزو على منتجاتي؟",
      },
      a: {
        en: "No. ISO does not permit use of its logo to imply certification. This guide concerns management-system certification, not product certification. Follow the issuing body’s rules for its own marks and for statements on packaging or marketing.",
        ar: "لا. لا تسمح ISO باستخدام شعارها للإيحاء بالحصول على شهادة منها. يتناول هذا الدليل شهادات أنظمة الإدارة، لا شهادات المنتجات. التزم بقواعد الجهة المانحة بشأن علاماتها والبيانات على العبوات أو في التسويق.",
      },
    },
  ],
};

const tableRows = [
  {
    c1: { en: "Minor nonconformity", ar: "عدم مطابقة صغرى" },
    c2: {
      en: "The body specifies the response and any effect on certification.",
      ar: "تحدد الجهة المانحة الاستجابة وأي أثر على الشهادة.",
    },
    c3: {
      en: "Submit the requested plan and evidence within the body’s deadline.",
      ar: "قدّم الخطة والأدلة المطلوبة ضمن مهلة الجهة المانحة.",
    },
  },
  {
    c1: { en: "Major nonconformity", ar: "عدم مطابقة كبرى" },
    c2: {
      en: "The body assesses the finding and may suspend certification.",
      ar: "تقيّم الجهة المانحة الملاحظة، وقد تعلّق الشهادة.",
    },
    c3: {
      en: "Fix the root cause and send evidence before the deadline.",
      ar: "عالج السبب الجذري وأرسل الأدلة قبل انتهاء المهلة.",
    },
  },
  {
    c1: { en: "Missed surveillance audit", ar: "تفويت تدقيق المراقبة" },
    c2: {
      en: "Failure to complete surveillance can lead to suspension or withdrawal.",
      ar: "قد يؤدي عدم استكمال المراقبة إلى التعليق أو السحب.",
    },
    c3: {
      en: "Contact the certification body at once and book a new date.",
      ar: "تواصل مع الجهة المانحة فوراً واحجز موعداً جديداً.",
    },
  },
  {
    c1: { en: "Recertification at year three", ar: "إعادة إصدار الشهادة في السنة الثالثة" },
    c2: {
      en: "Renewal needs successful assessment and a certification decision.",
      ar: "يتطلب التجديد تقييماً ناجحاً وقراراً من الجهة المانحة.",
    },
    c3: {
      en: "Allow time for the audit, corrective actions and decision before expiry.",
      ar: "اترك وقتاً للتدقيق والإجراءات التصحيحية والقرار قبل الانتهاء.",
    },
  },
  {
    c1: { en: "Certificate transfer", ar: "نقل الشهادة" },
    c2: {
      en: "The receiving body reviews eligibility and confirms the remaining cycle.",
      ar: "تراجع الجهة المستلمة الأهلية وتؤكد المدة المتبقية من الدورة.",
    },
    c3: {
      en: "Confirm status, relevant accreditation and outstanding findings.",
      ar: "أكّد الحالة والاعتماد ذي الصلة والملاحظات المفتوحة.",
    },
  },
];

export function LifecycleBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "Management-system certification commonly follows a three-year cycle with surveillance between certification and recertification. Use the dates on your certificate and the programme agreed with its issuer. Maintaining certification depends on continued conformity and the certification body’s decisions; it is not a one-time purchase.",
          ar: "تتبع شهادات أنظمة الإدارة عادةً دورة من ثلاث سنوات، مع تدقيق مراقبة بين المنح وإعادة المنح. اعتمد على التواريخ الواردة في شهادتك والبرنامج المتفق عليه مع مصدرها. ويتوقف استمرار الشهادة على استمرار المطابقة وقرارات الجهة المانحة؛ فهي ليست وثيقة تُشترى مرة واحدة.",
        })}{" "}
        {t({ en: "Start with our guide to ", ar: "ابدأ من دليلنا عن " })}
        <LangLink
          to="/insights/iso-certification-cost-and-process-in-iraq"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "ISO certification cost and process in Iraq",
            ar: "تكلفة وإجراءات شهادة الأيزو في العراق",
          })}
        </LangLink>
        {t({ en: ", or see ", ar: "، أو اطّلع على " })}
        <LangLink to="/services/iso-9001" className="font-semibold text-primary hover:underline">
          {t({ en: "ISO 9001 certification", ar: "شهادة الأيزو 9001" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>
      <P>
        {t({
          en: "This guide covers the four questions certified companies in Iraq ask most: what the surveillance audit checks, what nonconformities mean, how renewal works at year three, and how to transfer a certificate to another body. IraqCert manages this cycle for its clients. Certificates are issued by our partner body AQSR, accredited by UAF, an IAF MLA signatory, and verifiable on IAF CertSearch. Consulting stays separate from the certification audit, as ISO/IEC 17021-1 requires.",
          ar: "يجيب هذا الدليل عن الأسئلة الأربعة الأكثر تكراراً لدى الشركات الحاصلة على الشهادة في العراق: ماذا يفحص تدقيق المراقبة، وماذا تعني حالات عدم المطابقة، وكيف يتم التجديد في السنة الثالثة، وكيف تُنقل الشهادة إلى جهة مانحة أخرى. تدير عراق سيرت هذه الدورة كاملة لعملائها. تصدر الشهادات عن شريكنا AQSR المعتمد من هيئة الاعتماد UAF الموقعة على اتفاقية الاعتراف المتبادل في المنتدى الدولي للاعتماد IAF، ويمكن التحقق من الشهادات عبر IAF CertSearch. وتبقى الاستشارة منفصلة عن تدقيق منح الشهادة كما تشترط المواصفة ISO/IEC 17021-1.",
        })}
      </P>

      <H2>{t({ en: "What a Surveillance Audit Checks", ar: "ماذا يفحص تدقيق المراقبة" })}</H2>
      <P>
        {t({
          en: "Surveillance samples the certified management system, including relevant changes, previous findings and evidence that controls remain effective. Keep internal-audit, management-review and corrective-action records current. Confirm the visits and any additional assessment with the issuing body rather than assuming all schemes use identical schedules.",
          ar: "يفحص تدقيق المراقبة عينات من نظام الإدارة الحاصل على الشهادة، بما في ذلك التغييرات ذات الصلة والملاحظات السابقة وأدلة فاعلية الضوابط. حافظ على تحديث سجلات التدقيق الداخلي ومراجعة الإدارة والإجراءات التصحيحية. وأكّد الزيارات وأي تقييم إضافي مع الجهة المانحة بدلاً من افتراض جدول واحد لجميع البرامج.",
        })}
      </P>
      <P>
        {t({
          en: "Assign responsibility for records and corrective actions throughout the year. Preparation support can help organize that work, but neither tidy records nor early booking guarantees a favourable audit result.",
          ar: "حدّد المسؤوليات عن السجلات والإجراءات التصحيحية طوال السنة. قد يساعد دعم التحضير في تنظيم هذا العمل، لكن ترتيب السجلات والحجز المبكر لا يضمنان نتيجة إيجابية للتدقيق.",
        })}
      </P>
      <H2>
        {t({
          en: "Failing an Audit: Minor and Major Nonconformities",
          ar: "الإخفاق في التدقيق: عدم المطابقة الصغرى والكبرى",
        })}
      </H2>
      <P>
        {t({
          en: "The auditor records nonconformities, and the certification body determines the required response. Do not decide severity from a short example: a missing record may have different implications depending on the process and evidence. Obtain the finding, deadline and evidence requirements in writing.",
          ar: "يسجل المدقق حالات عدم المطابقة، وتحدد الجهة المانحة الاستجابة المطلوبة. لا تحدد جسامة الملاحظة من مثال مختصر؛ فقد تختلف آثار السجل المفقود بحسب العملية والأدلة. احصل على الملاحظة والمهلة والأدلة المطلوبة كتابةً.",
        })}
      </P>
      <P>
        {t({
          en: "Unresolved findings or missed required audits can affect certification status. The issuing body decides whether certification is maintained, suspended, restored, reduced in scope or withdrawn under its applicable rules. Corrective action does not restore a certificate until the body confirms its decision.",
          ar: "قد تؤثر الملاحظات غير المعالجة أو تفويت التدقيق المطلوب على حالة الشهادة. تقرر الجهة المانحة استمرار الشهادة أو تعليقها أو استعادتها أو تقليص نطاقها أو سحبها وفق القواعد المنطبقة. ولا يعيد الإجراء التصحيحي صلاحية الشهادة قبل تأكيد الجهة قرارها.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th className={thClass}>{t({ en: "Event", ar: "الحدث" })}</th>
            <th className={thClass}>{t({ en: "What happens", ar: "ماذا يحدث" })}</th>
            <th className={thClass}>
              {t({ en: "What you should do", ar: "ما الذي يجب أن تفعله" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr key={row.c1.en} className="border-t border-border">
              <td className={`${tdClass} font-semibold text-foreground`}>{t(row.c1)}</td>
              <td className={tdClass}>{t(row.c2)}</td>
              <td className={tdClass}>{t(row.c3)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <H2>
        {t({
          en: "Renewal at Year Three: The Recertification Audit",
          ar: "التجديد في السنة الثالثة: تدقيق إعادة إصدار الشهادة",
        })}
      </H2>
      <P>
        {t({
          en: "Plan recertification before the expiry date stated on the certificate. The body reviews continued conformity and performance across the cycle, including changes and unresolved findings. A visit alone is not renewal: required follow-up and the certification decision must also be completed.",
          ar: "خطّط لإعادة منح الشهادة قبل تاريخ انتهائها المبيّن عليها. تراجع الجهة المانحة استمرار المطابقة والأداء خلال الدورة، بما يشمل التغييرات والملاحظات غير المعالجة. فالزيارة وحدها ليست تجديداً؛ إذ يجب أيضاً استكمال المتابعة المطلوبة وقرار الشهادة.",
        })}
      </P>
      <P>
        {t({
          en: "Book early enough to allow assessment, corrective actions and the issuing body’s decision. IraqCert can help coordinate dates within the agreed support scope; it cannot guarantee uninterrupted validity. If a certificate expires, ask the issuer which restoration or reassessment route applies and describe the gap accurately to any buyer requiring a valid certificate.",
          ar: "احجز مبكراً بما يسمح بالتقييم والإجراءات التصحيحية وقرار الجهة المانحة. تستطيع عراق سيرت المساعدة في تنسيق المواعيد ضمن نطاق الدعم المتفق عليه، لكنها لا تضمن استمرار الصلاحية دون انقطاع. وإذا انتهت الشهادة، فاسأل مصدرها عن مسار الاستعادة أو إعادة التقييم المنطبق، ووضّح فترة الانقطاع بدقة لأي مشترٍ يشترط شهادة سارية.",
        })}
      </P>
      <H2>
        {t({
          en: "Transferring Your Certificate to Another Certification Body",
          ar: "نقل شهادتك إلى جهة مانحة أخرى",
        })}
      </H2>
      <P>
        {t({
          en: "Ask the proposed receiving body to review the certificate, accreditation scope, audit reports, expiry date and open findings before agreeing a transfer. It should confirm eligibility, any additional work and the cycle it will retain. Do not assume that booking with a new provider automatically transfers certification.",
          ar: "اطلب من الجهة المستلمة المقترحة مراجعة الشهادة ونطاق الاعتماد وتقارير التدقيق وتاريخ الانتهاء والملاحظات المفتوحة قبل الاتفاق على النقل. ينبغي أن تؤكد الأهلية وأي عمل إضافي والدورة التي ستُبقيها. لا تفترض أن الحجز لدى مقدم جديد ينقل الشهادة تلقائياً.",
        })}
      </P>
      <P>
        {t({
          en: "If the existing certificate is not eligible for the proposed accredited transfer route, ask the receiving body to specify the assessment needed. IraqCert coordinates preparation; eligibility and certification remain the responsibility of the receiving body.",
          ar: "إذا لم تكن الشهادة الحالية مؤهلة لمسار النقل المعتمد المقترح، فاطلب من الجهة المستلمة تحديد التقييم اللازم. تنسّق عراق سيرت التحضير، بينما تبقى الأهلية ومنح الشهادة من مسؤولية الجهة المستلمة.",
        })}
      </P>

      <H2>{t({ en: "Sources", ar: "المصادر" })}</H2>
      <P>
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "ISO: certification and accreditation", ar: "ISO: منح الشهادات والاعتماد" })}
        </a>
      </P>
      <P>
        <a
          href="https://www.iso.org/standard/61651.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "ISO: framework for management-system certification bodies",
            ar: "ISO: الإطار الخاص بجهات منح شهادات أنظمة الإدارة",
          })}
        </a>
      </P>
      <P>
        <a
          href="https://www.bsigroup.com/en-US/products-and-services/assessment-and-certification/system-certification/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "BSI: certification process and transfer review",
            ar: "BSI: إجراءات الشهادة ومراجعة النقل",
          })}
        </a>
      </P>
    </>
  );
}
